import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { Utilizador } from '@core/models/models';

@Component({
  selector: 'app-lista-utilizadores',
  standalone: true,
  imports: [CommonModule, PaginatorModule, SkeletonModule, MenuModule],
  templateUrl: './lista-utilizadores.component.html',
  styleUrl: './lista-utilizadores.component.scss',
})
export class ListaUtilizadoresComponent implements OnChanges {
  @Input() pagination: any;
  @Input() skeletonLoading = false;

  @Output() pageChange = new EventEmitter<any>();
  @Output() editar = new EventEmitter<Utilizador>();
  @Output() resetarSenha = new EventEmitter<Utilizador>();
  @Output() gerirGrupos = new EventEmitter<Utilizador>();

  readonly skeletonRows = [1, 2, 3, 4, 5];

  /**
   * Reconstruído sempre que `pagination` muda (ngOnChanges), nunca inline no
   * template dentro de *ngFor — chamar um factory de MenuItem[] a cada ciclo
   * de change detection provoca reinicializações do popup do PrimeNG.
   */
  menuItemsMap = new Map<number, MenuItem[]>();

  private readonly failedFotos = new Set<number>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pagination']) {
      this.buildMenuMap();
    }
  }

  private buildMenuMap(): void {
    this.menuItemsMap.clear();
    for (const user of this.pagination?.data ?? []) {
      if (user.id == null) continue;
      this.menuItemsMap.set(user.id, [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          command: () => this.editar.emit(user),
        },
        {
          label: 'Resetar senha',
          icon: 'pi pi-key',
          command: () => this.resetarSenha.emit(user),
        },
        {
          label: 'Gerir grupos',
          icon: 'pi pi-users',
          command: () => this.gerirGrupos.emit(user),
        },
      ]);
    }
  }

  onPageChange(event: any): void {
    this.pageChange.emit(event);
  }

  showFoto(user: { id?: number; fotografia?: string | null }): boolean {
    return !!user.fotografia && !this.failedFotos.has(user.id ?? -1);
  }

  onFotoError(user: { id?: number }): void {
    this.failedFotos.add(user.id ?? -1);
  }

  getAvatarColor(id: number): string {
    const colors = ['green', 'blue', 'orange', 'purple', 'teal', 'red'];
    return colors[(id || 0) % colors.length];
  }

  getInitials(nome: string): string {
    if (!nome) return '?';
    const parts = nome.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  getPerfilBadge(perfil: string | null | undefined): {
    classe: string;
    label: string;
  } {
    switch ((perfil || '').toUpperCase()) {
      case 'ADMIN':
        return { classe: 'perfil-admin', label: 'Admin' };
      case 'CLIENTE':
        return { classe: 'perfil-cliente', label: 'Cliente' };
      case 'EFETIVO':
        return { classe: 'perfil-efetivo', label: 'Efetivo' };
      default:
        return { classe: 'perfil-default', label: perfil || '—' };
    }
  }

  getEstadoBadge(isActive: boolean | null | undefined): {
    classe: string;
    label: string;
  } {
    if (isActive === false) {
      return { classe: 'estado-inativo', label: 'Inativo' };
    }
    return { classe: 'estado-ativo', label: 'Ativo' };
  }
}
